/**
 * ReplyComposer — the member's reply control on a conversation (040 US1/US2;
 * restyled and unified by 055).
 *
 * ── What 055 changed ────────────────────────────────────────────────────────
 *
 * The former separate `AttachmentUpload` control is FOLDED IN: attaching and
 * sending are now one action, as in the prototype. The member stages files,
 * optionally writes text, and presses send once.
 *
 * A reply may be TEXT-ONLY, ATTACHMENT-ONLY, or both (FR-018a). Only "neither"
 * is refused. Attachment-only matters in practice — "here is the contract you
 * asked for" with the file attached previously forced the member to type filler.
 *
 * ── Why an attachment-only reply creates no message ─────────────────────────
 *
 * The message and each attachment are SEPARATE operations with no association
 * between them, so empty text anchors nothing: the files simply appear in the
 * conversation's attachment list. A placeholder like "(liite lähetetty)" would
 * put words in the member's mouth (P VII), so callers must NOT treat "no new
 * message after send" as a failure.
 *
 * ── Behaviour that must not be lost in the restyle ──────────────────────────
 *
 * On success the parent RE-READS the conversation (route-on-read, P7); nothing is
 * appended locally. Typed text survives a failure (FR-004). The send control is
 * NEVER `disabled` (a disabled element is blurred — the WCAG 2.4.3 bug 039
 * fixed); `aria-busy` plus a synchronous ref guard prevent double-submit. The
 * prototype disables its button; that is the one part NOT to copy.
 *
 * The client-side type/size pre-check is ADVISORY — the server validates
 * authoritatively and refuses even if the pre-check is bypassed (FR-008/FR-009).
 * There is NO member delete of an already-uploaded attachment (FR-013); removing
 * a STAGED file before sending is a different thing and is offered.
 */

import { type JSX, useId, useRef, useState } from 'react';

import { localeTag } from '../i18n/localeTag.js';
import { useLanguage } from '../i18n/useLanguage.js';
import { useTranslate } from '../i18n/useTranslate.js';
import {
  attachmentBlockReason,
  MEMBER_ATTACHMENT_MAX_BYTES,
  MEMBER_ATTACHMENT_MAX_COUNT,
  MEMBER_REPLY_MAX_CHARS,
  replyLength,
} from './attachment-limits.js';
import { AttachControl, StagedAttachmentList, type StagedFile } from './StagedAttachmentList.js';
import { useReannouncingState } from './useReannouncingState.js';

const sendButtonClasses =
  'inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2 text-sm ' +
  'font-medium text-primary-foreground transition hover:bg-primary/90 focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';
const attachButtonClasses =
  'inline-flex items-center justify-center gap-2 rounded-md border border-border px-3 py-2 ' +
  'text-sm font-medium text-foreground transition hover:bg-muted focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-ring';

/** The immediate state of the composer (view-state only; nothing persisted). */
type ComposerState =
  | 'idle'
  | 'sending'
  | 'sent'
  | 'sent-with-attachment'
  | 'failed'
  | 'upload-failed'
  | 'empty'
  | 'too-long'
  | 'too-large'
  | 'unsupported'
  | 'too-many';

/** Send failures a mid-send file refusal must never overwrite. */
const FAILURES = new Set<ComposerState>(['failed', 'upload-failed', 'too-large', 'unsupported']);

export function ReplyComposer({
  caseId,
  addMessage,
  uploadAttachment,
  onReplyAdded,
}: {
  readonly caseId: string;
  readonly addMessage: (
    caseId: string,
    text: string,
  ) => Promise<'ok' | 'not-found' | 'unavailable'>;
  /**
   * Optional: when absent the composer is text-only (no attach control is
   * offered). The conversation detail passes it whenever the port exposes it.
   */
  readonly uploadAttachment?: (
    caseId: string,
    file: File,
  ) => Promise<'ok' | 'too-large' | 'unsupported' | 'not-found' | 'unavailable'>;
  /** Called after a successful send so the parent re-reads the detail (route-on-read). */
  readonly onReplyAdded: () => void;
}): JSX.Element {
  const t = useTranslate();
  const [text, setText] = useState('');
  // Staged files carry a generated id, not an index or filename (neither is a
  // stable, unique key when chips are removed or two files share a name).
  const [files, setFiles] = useState<readonly StagedFile[]>([]);
  const nextStagedIdRef = useRef(0);
  const [state, setState, announce] = useReannouncingState<ComposerState>('idle');
  const textareaId = useId();
  const statusId = useId();
  const counterId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Synchronous in-flight guard: the control is never `disabled` (it would blur),
  // and a stale-closure `state` check cannot dedupe rapid double-sends.
  const inFlightRef = useRef(false);
  const pendingNoteRef = useRef<ComposerState | null>(null);
  // Latest staged list: the composer stays interactive mid-send, so the upload
  // loop skips chips removed meanwhile, and end-of-send updates are functional.
  const filesRef = useRef(files);
  filesRef.current = files;
  const attachButtonRef = useRef<HTMLButtonElement>(null);

  const measuredLength = replyLength(text.trim()); // as the send check and the server measure it
  const overLimit = MEMBER_REPLY_MAX_CHARS !== undefined && measuredLength > MEMBER_REPLY_MAX_CHARS;
  // Only the text's own refusals mark the textarea invalid (not upload refusals);
  // over-limit is exposed as it is typed, not only after a send attempt.
  const isInvalid = state === 'empty' || state === 'too-long' || overLimit;
  const nearLimit =
    MEMBER_REPLY_MAX_CHARS !== undefined && measuredLength >= MEMBER_REPLY_MAX_CHARS * 0.9;
  const formatCount = new Intl.NumberFormat(localeTag(useLanguage().active));

  // Also runs while already 'idle': the write cancels a pending re-announce, so a
  // refusal the member has just acted on never reappears.
  const clearNote = (): void => {
    if (state !== 'sending') setState('idle');
  };

  const onPickFiles = (list: FileList | null): void => {
    if (list === null) {
      return;
    }
    const picked = [...list];
    // Mid-send, never touch the busy state: a refusal is shown once the send settles.
    const report = (next: ComposerState): void => {
      if (!inFlightRef.current) announce(next);
      else pendingNoteRef.current = next === 'idle' ? null : next; // only the latest pick counts
    };
    // Advisory pre-check per file; the server re-validates authoritatively.
    for (const file of picked) {
      const reason = attachmentBlockReason(file, MEMBER_ATTACHMENT_MAX_BYTES);
      if (reason !== null) {
        report(reason);
        return;
      }
    }
    const next: readonly StagedFile[] = [
      ...files,
      ...picked.map((file) => ({ id: `staged-${nextStagedIdRef.current++}`, file })),
    ];
    // 055 FR-016a: an undefined count limit means no enforcement AND no hint.
    if (MEMBER_ATTACHMENT_MAX_COUNT !== undefined && next.length > MEMBER_ATTACHMENT_MAX_COUNT) {
      report('too-many');
      setFiles(next.slice(0, MEMBER_ATTACHMENT_MAX_COUNT));
      return;
    }
    report('idle');
    setFiles(next);
  };

  const onSend = async (): Promise<void> => {
    if (inFlightRef.current) {
      return;
    }
    const trimmed = text.trim();
    // FR-018a: text OR at least one attachment. Only "neither" is refused, and
    // the note names both ways forward.
    if (trimmed === '' && files.length === 0) {
      announce('empty');
      return;
    }
    if (MEMBER_REPLY_MAX_CHARS !== undefined && replyLength(trimmed) > MEMBER_REPLY_MAX_CHARS) {
      announce('too-long');
      return;
    }
    inFlightRef.current = true;
    setState('sending');
    // Parts already committed on THIS send: a later failure must not claim the
    // whole reply failed, must not re-stage them, and must still re-read.
    let committed = false;
    const uploadedIds = new Set<string>();
    const dropUploaded = (): void => {
      setFiles((prev) => prev.filter((other) => !uploadedIds.has(other.id)));
    };
    const stopAfterPartial = (failure: ComposerState): void => {
      dropUploaded();
      setState(failure);
      if (committed) {
        onReplyAdded();
      }
    };
    try {
      // 1. The message, only when there IS text (see header: nothing synthesised).
      if (trimmed !== '') {
        const outcome = await addMessage(caseId, trimmed);
        if (outcome !== 'ok') {
          // Honest retryable failure; text and staged files are kept (FR-004).
          setState('failed');
          return;
        }
        committed = true;
        setText((prev) => (prev.trim() === trimmed ? '' : prev)); // mid-send edits survive (FR-004)
      }
      // 2. The attachments, one at a time (server/sniff authoritative). Each
      //    uploaded file leaves the staged list, so a retry re-sends only the rest.
      for (const staged of files) {
        if (uploadAttachment === undefined) {
          break;
        }
        if (!filesRef.current.some((other) => other.id === staged.id)) {
          continue; // removed by the member while the send was in flight
        }
        const outcome = await uploadAttachment(caseId, staged.file);
        if (outcome === 'too-large' || outcome === 'unsupported') {
          stopAfterPartial(outcome);
          return;
        }
        if (outcome !== 'ok') {
          stopAfterPartial('upload-failed');
          return;
        }
        committed = true;
        uploadedIds.add(staged.id);
      }
      if (!committed) {
        // Defensive (unreachable via the UI: staging needs `uploadAttachment`, and
        // the first file uploads before any await): never claim "sent" (P7).
        setState('upload-failed');
        return;
      }
      const hadAttachments = uploadedIds.size > 0;
      dropUploaded(); // no second setText(''): text typed mid-send survives (FR-004)
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
      // Not downloadable until its scan clears, so say so, not a bare "sent" (FR-019).
      setState(hadAttachments ? 'sent-with-attachment' : 'sent');
      onReplyAdded(); // live re-read, never a local append (P7)
    } catch {
      if (committed) {
        stopAfterPartial('upload-failed');
      } else {
        setState(trimmed === '' ? 'upload-failed' : 'failed'); // no text → the throw was an upload
      }
    } finally {
      inFlightRef.current = false;
      const pending = pendingNoteRef.current;
      pendingNoteRef.current = null;
      // A mid-send refusal may replace a success note, never the send's failure.
      if (pending !== null) setState((prev) => (FAILURES.has(prev) ? prev : pending));
    }
  };

  const note = ((): string | null => {
    switch (state) {
      case 'sent':
        return t('myCases.detail.replySent');
      case 'sent-with-attachment':
        return t('myCases.detail.uploadBeingChecked');
      case 'failed':
        return t('myCases.detail.replyFailed');
      case 'upload-failed':
        return t('myCases.detail.uploadFailed');
      case 'empty':
        return t('myCases.detail.replyEmpty');
      case 'too-long':
        return t('myCases.detail.replyTooLong');
      case 'too-large':
        return t('myCases.detail.uploadTooLarge');
      case 'unsupported':
        return t('myCases.detail.uploadUnsupported');
      case 'too-many':
        return t('myCases.detail.uploadTooMany');
      default:
        return null;
    }
  })();

  return (
    <div className="mt-6 space-y-2">
      <label htmlFor={textareaId} className="block text-sm font-semibold text-foreground">
        {t('myCases.detail.replyLabel')}
      </label>
      <textarea
        id={textareaId}
        className={
          'min-h-32 w-full resize-y rounded-lg border bg-card p-3 text-sm text-foreground ' +
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ' +
          (isInvalid
            ? 'border-destructive focus-visible:ring-destructive'
            : 'border-border focus-visible:ring-ring')
        }
        placeholder={t('myCases.detail.replyPlaceholder')}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          clearNote();
        }}
        aria-busy={state === 'sending'}
        aria-describedby={
          MEMBER_REPLY_MAX_CHARS !== undefined ? `${statusId} ${counterId}` : statusId
        }
        {...(isInvalid ? { 'aria-invalid': true } : {})}
      />

      {/* 055 FR-016a: no counter at all when the limit is configured as absent. */}
      {MEMBER_REPLY_MAX_CHARS !== undefined ? (
        <p
          id={counterId}
          className={`text-right text-xs ${
            overLimit
              ? 'font-semibold text-foreground'
              : nearLimit
                ? 'font-medium text-status-progress-foreground'
                : 'text-muted-foreground'
          }`}
        >
          {formatCount.format(measuredLength)} / {formatCount.format(MEMBER_REPLY_MAX_CHARS)}
          <span className="sr-only"> {t('myCases.detail.charCountUnit')}</span>
        </p>
      ) : null}

      {uploadAttachment !== undefined ? (
        <>
          {/* Visually hidden native input, triggered by the styled button. */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            tabIndex={-1} // the styled button is the tab stop (WCAG 2.4.7)
            className="sr-only"
            aria-hidden="true" // opened only via the attach button, which carries the rule
            aria-label={t('myCases.detail.uploadLabel')}
            onChange={(e) => {
              onPickFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <StagedAttachmentList
            files={files}
            onRemove={(id) => {
              setFiles((prev) => prev.filter((other) => other.id !== id));
              clearNote();
            }}
            fallbackFocusRef={attachButtonRef}
          />
        </>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 pt-1">
        {uploadAttachment !== undefined ? (
          <AttachControl
            buttonRef={attachButtonRef}
            className={attachButtonClasses}
            onAttach={() => fileInputRef.current?.click()}
            maxCount={MEMBER_ATTACHMENT_MAX_COUNT}
            stagedCount={files.length}
          />
        ) : (
          <span />
        )}
        <button
          type="button"
          className={`${sendButtonClasses} ml-auto`}
          onClick={() => {
            void onSend();
          }}
          aria-busy={state === 'sending'}
        >
          {state === 'sending' ? t('myCases.detail.replySending') : t('myCases.detail.replySend')}
        </button>
      </div>

      {/* Polite live region: announces success, a retryable failure, or a
          validation refusal exactly once. Focus stays on the composer, so the
          outcome would otherwise be a silent visual change (WCAG 4.1.3). */}
      <div id={statusId} role="status" aria-live="polite">
        {note !== null ? (
          <p
            className={
              state === 'sent' || state === 'sent-with-attachment'
                ? 'mt-1 text-xs text-muted-foreground'
                : // Errors: the red tint + border carry the cue; foreground text clears 4.5:1.
                  'mt-1 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-foreground'
            }
          >
            {note}
          </p>
        ) : null}
      </div>
    </div>
  );
}
